import { FeatureFlag } from '@ps/web-core-enums';
import type { Currency } from '@ps/web-core-enums';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import useFeature from '@swf/core/hooks/useFeature';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';

import type { Limits } from '../../types/responsibleGamingLimits';
import { getDefaultCurrency } from '../../utils/responsibleGamingLimits';
import Alert from '../Alert/Alert';
import CTAButton from '../CTAButton/CTAButton';

import {
  TXTCLI_You_are_required_to_set_at_lea_ELL,
  TXTWCL_Continue_without_deposit_limit_ELL,
  TXTWCL_Continue_without_loss_limits,
  TXTWCL_You_are_required_to_set_a_depo_ELL
} from './ResponsibleGamingLimits.messages.json';
import { fieldsWrapper, noSwitch } from './ResponsibleGamingLimits.scss';
import DailyLimit from './components/DailyLimit/DailyLimit';
import MonthlyLimit from './components/MonthlyLimit/MonthlyLimit';
import Switch from './components/Switch/Switch';
import WeeklyLimit from './components/WeeklyLimit/WeeklyLimit';

interface Props {
  fieldName: string;
  limits: Limits;
}

const ResponsibleGamingLimits: React.FC<Props> = ({ fieldName, limits }) => {
  const form = useForm();
  const request = useRequest();
  const site = useSelector(select.cms.getSiteId);
  const [noLimitsSet, setNoLimitsSet] = useState(false);
  const swcRmfNoLimits = useFeature(FeatureFlag.SwcRmfNoLimits);
  const swcRmfShowOnlyWeeklyLimit = useFeature(FeatureFlag.SwcRmfShowOnlyWeeklyLimit);
  const defaultSystemCurrency = getDefaultCurrency(site);
  const defaultUserCurrency = (useSelector(select.account.getDefaultCurrencyForCountry) ||
    defaultSystemCurrency) as Currency;
  const isMultiCurrency = defaultSystemCurrency !== defaultUserCurrency;

  const {
    input: { value: dailyLimit }
  } = useField(`${fieldName}.daily.limit`, { subscription: { value: true } });
  const {
    input: { value: weeklyLimit }
  } = useField(`${fieldName}.weekly.limit`, { subscription: { value: true } });
  const {
    input: { value: monthlyLimit }
  } = useField(`${fieldName}.monthly.limit`, { subscription: { value: true } });
  useField(`${fieldName}.rate`, { initialValue: null });
  const {
    input: { value: currency }
  } = useField(`${fieldName}.currency`, {
    subscription: { value: true },
    initialValue: defaultUserCurrency
  });

  useEffect(() => {
    request({
      service: 'wc3.currency.get.zmFxRates',
      body: { fromCurrency: defaultUserCurrency, toCurrency: defaultSystemCurrency }
    }).subscribe(
      ({
        body: {
          rates: [{ rate }]
        }
      }) => form.change(`${fieldName}.rate`, rate)
    );
  }, [defaultSystemCurrency, defaultUserCurrency, fieldName, form, request]);

  const handleSubmit = useCallback(() => {
    if (!dailyLimit && !weeklyLimit && !monthlyLimit) {
      setNoLimitsSet(true);
      return;
    }

    form.change(`${fieldName}.skip`, false);
    form.submit();
  }, [dailyLimit, fieldName, form, monthlyLimit, weeklyLimit]);

  const skipLimits = useCallback(() => {
    form.change(`${fieldName}.skip`, true);
    form.submit();
  }, [fieldName, form]);

  return (
    <>
      {isMultiCurrency && <Switch currency={defaultUserCurrency} fieldName={fieldName} />}
      {noLimitsSet && (
        <Alert variant="error">
          <Translation
            {...(!swcRmfShowOnlyWeeklyLimit
              ? TXTCLI_You_are_required_to_set_at_lea_ELL
              : TXTWCL_You_are_required_to_set_a_depo_ELL)}
          />
        </Alert>
      )}
      <div className={classNames(fieldsWrapper, { [noSwitch]: !isMultiCurrency && !noLimitsSet })}>
        {!swcRmfShowOnlyWeeklyLimit && (
          <DailyLimit limits={limits} fieldName={fieldName} currency={currency} />
        )}
        <WeeklyLimit limits={limits} fieldName={fieldName} currency={currency} />
        {!swcRmfShowOnlyWeeklyLimit && (
          <MonthlyLimit limits={limits} fieldName={fieldName} currency={currency} />
        )}
        <CTAButton type="button" onClick={handleSubmit} />
        {swcRmfNoLimits && (
          <CTAButton
            type="button"
            variant="text"
            onClick={skipLimits}
            translation={
              fieldName === 'rmf.deposit'
                ? TXTWCL_Continue_without_deposit_limit_ELL
                : TXTWCL_Continue_without_loss_limits
            }
          />
        )}
      </div>
    </>
  );
};

export default ResponsibleGamingLimits;
